import { GridColDef, GridCellParams } from '@mui/x-data-grid';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Tooltip } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getAllMemberAutogiroStatus } from 'src/shared/services/memberAutogiroStatus';
import { MemberAutogiroStatusResponse } from 'src/modules/member/models/memberAutogiroStatusResponse';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { displayAutogiroStatus } from 'src/shared/models/enums/autogiroPeriodicity.enum';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { MemberAutogiroStatus } from '../models/memberAutogiroStatus';

type MemberAutogiroStatusDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: MemberAutogiroStatus;
  searchFilterCallback: (filter: MemberAutogiroStatus) => void;
};

export const MemberAutogiroStatusDataGrid = (props: MemberAutogiroStatusDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const { dateStamp } = useGlobals();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  // För framtida hantering [onRowClick]
  // const [modalOpen, setModalOpen] = useState<boolean>(false);
  // const [selectedItem, setSelectedItem] = useState<MemberAutogiroStatusResponse>();

  const [rows, setRows] = useState<MemberAutogiroStatusResponse[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllMemberAutogiroStatus(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          ConsoleHelper.log(dateStamp);
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.autogiroStatus_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter, dateStamp]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  // const onRowClick = (params) => {
  //   setSelectedItem(params.row);
  //   setModalOpen(true);
  // };

  const renderMandateWarning = (row: MemberAutogiroStatusResponse) => {
    if (!row.hasMandate) {
      return (
        <Tooltip title={t('invoice:autogiro.noMandateWarning')} arrow placement='left'>
          <Warning color='warning' sx={{ fontSize: 20 }} />
        </Tooltip>
      );
    }
    if (row.mandateStatus !== AutogiroMandateStatusEnum.Approved) {
      return (
        <Tooltip title={t('invoice:autogiro.mandateNotApprovedWarning')} arrow placement='left'>
          <Warning color='error' sx={{ fontSize: 20 }} />
        </Tooltip>
      );
    }
    return null;
  };

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('invoice:autogiro.tableHeader.memberNr'),
      sortable: true,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return <Link to={`${RoutesEnum.MemberDetails}/${params.row.customer_id}`}>{params.row.memberNr}</Link>;
      },
    },
    {
      field: 'firstName',
      headerName: t('invoice:autogiro.tableHeader.firstName'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:autogiro.tableHeader.lastName'),
      sortable: true,
      flex: 1.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'periodicity',
      headerName: t('invoice:autogiro.tableHeader.periodicity'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{displayAutogiroStatus(params.row, t)}</>;
      },
    },
    {
      field: 'created',
      headerName: t('invoice:autogiro.tableHeader.createdAt'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.created)}</>;
      },
    },
    {
      field: 'nextInvoiceAfter',
      headerName: t('invoice:autogiro.tableHeader.nextInvoiceAfter'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const warning = renderMandateWarning(params.row);
        const isRed = params.row.hasMandate && params.row.mandateStatus !== AutogiroMandateStatusEnum.Approved;
        return (
          <Box display='flex' alignItems='center' gap={0.5}>
            <span style={isRed ? { textDecoration: 'line-through' } : undefined}>{DateHelper.getDate(params.row.nextInvoiceAfter)}</span>
            {warning}
          </Box>
        );
      },
    },
    {
      field: 'nextInvoiceAmount',
      headerAlign: 'left',
      align: 'left',
      headerName: t('invoice:autogiro.tableHeader.amount'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        if (params.row.nextInvoiceAmount === -1) return <>{t('invoice:autogiro.amountNotDetermined')}</>;
        return <AmountWithCurrency amount={params.row.nextInvoiceAmount} />;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
            py: 1,
          },
        }}
        ref={scrollRef}
      >
        <DataGrid<MemberAutogiroStatusResponse>
          disablePointer
          checkboxSelection={false}
          disableSelectionOnClick
          columns={columns}
          rows={rows}
          autoHeight
          dataModel={data}
          columnVisibilityModel={{
            autogiroMandate_id: false,
          }}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={[{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length > 0) {
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: newSortModel[0].field,
                sortDir: newSortModel[0].sort,
              });
            } else {
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: searchFilter.sortCol,
                sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
              });
            }
          }}
          // onRowClick={onRowClick}
          selectionModel={[]}
          onSelectionModelChange={() => ConsoleHelper.log('')}
        />
      </Box>
    </>
  );
};
